import React, { useState, useEffect } from "react";
import {
  CreditCard,
  Wallet,
  CalendarDays,
  Filter,
  FileText,
  Download,
} from "lucide-react";
import { getMyPurchases } from "../../api/api";

const PaymentHistory = () => {
  const [activeTab, setActiveTab] = useState("Appointments");
  const [purchases, setPurchases] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const TABS = ["Appointments", "Packages"];

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await getMyPurchases(1, 100);
      
      if (response && response.status === true) {
        setPurchases(response.purchases || []);
        setAppointments(response.appointmentHistory || []);
      }
      setError(null);
    } catch (err) {
      setError("Failed to load payment history");
      console.error("Error fetching history:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount, isPaise = false) => {
    if (!amount) return '$0';
    let numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isPaise) {
      numAmount = numAmount / 100;
    }
    return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusColor = (status) => {
    if (status === 'success' || status === 'Successful' || status === 'completed' || status === 'active') {
      return "bg-[#57b26a]";
    }
    if (status === 'pending' || status === 'created' || status === 'Pending') {
      return "bg-[#ff8c00]";
    }
    if (status === 'failed' || status === 'Failed') {
      return "bg-[#ff4444]";
    }
    return "bg-[#57b26a]";
  };

  const getStatusText = (status) => {
    if (status === 'success' || status === 'Successful' || status === 'completed') return "Paid";
    if (status === 'pending' || status === 'created' || status === 'Pending') return "Pending";
    if (status === 'failed' || status === 'Failed') return "Failed";
    if (status === 'active') return "Active";
    return status || "Pending";
  };

  const getTotalSpent = () => {
    let total = 0;
    
    // Calculate from purchases
    purchases.forEach(purchase => {
      const amount = parseFloat(purchase.total_amount || purchase.amount || 0);
      total += amount;
    });
    
    // Calculate from appointments
    appointments.forEach(appointment => {
      const paymentDetail = appointment.paymentDetails?.[appointment.paymentDetails.length - 1];
      if (paymentDetail?.amount) {
        total += paymentDetail.amount / 100;
      }
    });
    
    return total.toFixed(2);
  };

  const renderAppointmentRow = (appointment) => {
    const paymentDetail = appointment.paymentDetails?.[appointment.paymentDetails.length - 1];
    const amount = paymentDetail?.amount || 0;
    const paymentStatus = paymentDetail?.status || appointment.payment_status || 'pending';
    const invoiceId = paymentDetail?.receipt || paymentDetail?.order_id || `INV-${appointment.id}`;
    
    return {
      id: appointment.id,
      date: formatDate(appointment.appointment_date || appointment.createdAt),
      invoice: invoiceId,
      desc: appointment.purpose || "Consultation",
      amount: formatCurrency(amount, true),
      status: getStatusText(paymentStatus),
      type: 'appointment'
    };
  };

  const renderPackageRow = (purchase) => {
    const latestPayment = purchase.payment_history?.[0];
    const paymentStatus = latestPayment?.payment_status || purchase.payment_status || 'pending';
    const amount = latestPayment?.amount || purchase.total_amount || purchase.amount || 0;
    const invoiceId = latestPayment?.invoice_number || `INV-PKG-${purchase.id}`;
    const programName = purchase.metadata?.program_name || purchase.program?.name || "Health Program";
    const duration = purchase.metadata?.variant_duration || purchase.variant?.duration || '';
    const desc = duration ? `${programName} (${duration})` : programName;
    
    return {
      id: purchase.id,
      date: formatDate(purchase.createdAt),
      invoice: invoiceId,
      desc: desc,
      amount: formatCurrency(amount, false),
      status: getStatusText(paymentStatus),
      type: 'package'
    };
  };

  const getDisplayData = () => {
    if (activeTab === "Packages") {
      return purchases.map(purchase => renderPackageRow(purchase));
    } else {
      return appointments.map(appointment => renderAppointmentRow(appointment));
    }
  };

  const displayData = getDisplayData();
  const totalSpent = getTotalSpent();

  if (loading) {
    return (
      <div className="font-lato">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
          Payment History
        </h2>
        <p className="text-[#97a09a] text-sm mb-8">
          View your past transactions, download invoices, and manage your billing details.
        </p>
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#57b26a]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="font-lato">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
          Payment History
        </h2>
        <p className="text-[#97a09a] text-sm mb-8">
          View your past transactions, download invoices, and manage your billing details.
        </p>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600">{error}</p>
          <button 
            onClick={fetchHistory}
            className="mt-4 px-4 py-2 bg-[#57b26a] text-white rounded-md"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="font-lato">
      <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
        Payment History
      </h2>
      <p className="text-[#97a09a] text-sm mb-8">
        View your past transactions, download invoices, and manage your billing details.
      </p>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-[#e8ece8]">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 px-4 text-[14px] font-medium transition-all ${
              activeTab === tab
                ? "text-[#57b26a] border-b-2 border-[#57b26a]"
                : "text-[#97a09a] hover:text-[#24312d]"
            }`}
          >
            {tab} ({activeTab === "Packages" ? purchases.length : appointments.length})
          </button>
        ))}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[12px] text-[#9aa39d] mb-3">
                Total Spent (All Time)
              </p>
              <h3 className="text-[34px] font-semibold text-[#24312d] leading-none">
                ${totalSpent}
              </h3>
              <p className="text-[12px] text-[#9aa39d] mt-4">
                {displayData.length} total transactions
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-[#f7f9f7] flex items-center justify-center">
              <Wallet size={18} className="text-[#57b26a]" />
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-3">
            <button className="h-[40px] px-4 border border-[#e1e6e1] rounded-lg text-[13px] text-[#24312d] flex items-center gap-2">
              <CalendarDays size={14} />
              All Time
            </button>
            <button className="h-[40px] px-4 border border-[#e1e6e1] rounded-lg text-[13px] text-[#24312d] flex items-center gap-2">
              <Filter size={14} />
              All Statuses
            </button>
          </div>
          <button className="h-[40px] px-4 border border-[#dfe5df] rounded-lg text-[13px] text-[#57b26a] flex items-center gap-2 hover:bg-green-50">
            <Download size={14} />
            Export CSV
          </button>
        </div>

        {displayData.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#97a09a]">No {activeTab.toLowerCase()} found</p>
          </div>
        ) : (
          <>
            {/* Desktop View */}
            <div className="hidden lg:block overflow-x-auto">
              <div className="min-w-[1000px]">
                <div className="grid grid-cols-6 pb-3 border-b border-[#edf1ed] text-[12px] text-[#97a09a] font-medium">
                  <span>DATE</span>
                  <span>INVOICE ID</span>
                  <span>DESCRIPTION</span>
                  <span>AMOUNT</span>
                  <span>STATUS</span>
                  <span>ACTION</span>
                </div>

                {displayData.map((item) => (
                  <div
                    key={`${item.type}-${item.id}`}
                    className="grid grid-cols-6 items-center py-4 border-b border-[#f4f6f4] last:border-0"
                  >
                    <span className="text-[13px] text-[#97a09a]">{item.date}</span>
                    <span className="text-[13px] text-[#bcc3bd]">{item.invoice}</span>
                    <span className="text-[14px] text-[#24312d]">{item.desc}</span>
                    <span className="text-[14px] font-medium text-[#24312d]">{item.amount}</span>
                    <span>
                      <span className={`inline-flex px-3 py-1 rounded-full text-white text-[11px] ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </span>
                    <span>
                      <button className="w-8 h-8 rounded-md border border-[#edf1ed] flex items-center justify-center hover:bg-gray-50">
                        <FileText size={14} className="text-[#8f9993]" />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile View */}
            <div className="grid lg:hidden gap-4">
              {displayData.map((item) => (
                <div key={`${item.type}-${item.id}`} className="border border-[#edf1ed] rounded-xl p-4">
                  <div className="flex justify-between mb-2">
                    <h4 className="text-[14px] font-medium text-[#24312d]">{item.amount}</h4>
                    <span className={`px-3 py-1 rounded-full text-white text-[11px] ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                  <p className="text-[13px] text-[#24312d] mb-1">{item.desc}</p>
                  <p className="text-[12px] text-[#97a09a]">{item.invoice}</p>
                  <p className="text-[12px] text-[#97a09a] mt-1">{item.date}</p>
                  <button className="mt-4 h-[36px] px-4 border border-[#dfe5df] rounded-md text-[12px] text-[#24312d] flex items-center gap-2">
                    <FileText size={13} />
                    Invoice
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;